import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Package, FileText, CreditCard, User as UserIcon, Clock, ChevronRight, ShoppingCart, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { ordersApi, Order as ApiOrder } from '../api/orders';
import { quotesApi, QuoteRequest } from '../api/quotes';

function transformApiOrder(apiOrder: ApiOrder) {
  return {
    id: apiOrder.order_number,
    type: apiOrder.type,
    items: apiOrder.items.map(item => ({
      product: {
        id: String(item.product_id),
        category: 'vetements' as const,
        subcategory: 'vestes' as const,
        sector: [],
        name: `Product ${item.product_id}`,
        nameEn: `Product ${item.product_id}`,
        brand: '',
        ref: '',
        price: item.unit_price,
        currency: 'EUR',
        stock: 0,
        moq: 1,
        quoteThreshold: 0,
        norm: [],
        description: '',
        descriptionEn: '',
        images: [],
        variants: {},
        features: [],
        featuresEn: [],
      },
      qty: item.quantity,
    })),
    date: apiOrder.created_at,
    status: apiOrder.status,
    form: apiOrder.form_data || {},
  };
}

interface DashboardStats {
  totalOrders: number;
  activeQuotes: number;
  creditLimit: number | null;
  accountManager: {
    name: string;
    email: string;
    phone: string;
  } | null;
}

export default function ResellerDashboard() {
  usePageTitle("Mon espace revendeur — Lodiss");

  const { lang, setPage, currentUser, addToCart } = useApp();
  const T = t[lang];

  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    activeQuotes: 0,
    creditLimit: null,
    accountManager: null,
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      ordersApi.getOrders(),
      quotesApi.getQuotes(),
    ])
      .then(([orders, quotes]) => {
        const activeQuotes = quotes.filter(q => q.status === 'pending' || q.status === 'reviewed').length;
        setStats(s => ({
          ...s,
          totalOrders: orders.length,
          activeQuotes,
          creditLimit: currentUser?.credit_limit || null,
          accountManager: currentUser?.account_manager_name ? {
            name: currentUser.account_manager_name,
            email: currentUser.account_manager_email || '',
            phone: currentUser.account_manager_phone || '',
          } : null,
        }));
        setRecentOrders(orders.slice(0, 3).map(transformApiOrder));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentUser]);

  const handleReorder = (order: any) => {
    order.items.forEach((item: any) => {
      addToCart({ product: item.product, qty: item.qty });
    });
    setPage('cart');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-700';
      case 'confirmed':
      case 'accepted': return 'bg-emerald-50 text-emerald-700';
      case 'shipped': return 'bg-blue-50 text-blue-700';
      case 'delivered': return 'bg-gray-50 text-gray-700';
      case 'cancelled':
      case 'rejected': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-950">
            {lang === 'fr' ? 'Bienvenue' : 'Welcome'}, {currentUser?.full_name}!
          </h1>
          <p className="text-gray-500 mt-1">
            {currentUser?.company || (lang === 'fr' ? 'Espace Revendeur' : 'Reseller Portal')}
          </p>
        </div>

        {/* Stats Widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-lodiss-red/10 flex items-center justify-center">
                <Package size={20} className="text-lodiss-red" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-950">{stats.totalOrders}</p>
                <p className="text-sm text-gray-500">
                  {lang === 'fr' ? 'Commandes' : 'Orders'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <FileText size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-950">{stats.activeQuotes}</p>
                <p className="text-sm text-gray-500">
                  {lang === 'fr' ? 'Devis en attente' : 'Pending quotes'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CreditCard size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-950">
                  {stats.creditLimit ? formatPrice(stats.creditLimit) : '-'}
                </p>
                <p className="text-sm text-gray-500">
                  {lang === 'fr' ? 'Crédit' : 'Credit limit'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <UserIcon size={20} className="text-blue-600" />
              </div>
              <div>
                {stats.accountManager ? (
                  <>
                    <p className="text-lg font-bold text-gray-950 truncate max-w-[150px]">
                      {stats.accountManager.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {lang === 'fr' ? 'Votre contact' : 'Account manager'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold text-gray-400">-</p>
                    <p className="text-sm text-gray-500">
                      {lang === 'fr' ? 'Compte en attente' : 'Pending assignment'}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-950 mb-4">
            {lang === 'fr' ? 'Commande rapide' : 'Quick order'}
          </h2>
          <button
            onClick={() => setPage('reseller-catalogue')}
            className="flex items-center gap-2 px-6 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors"
          >
            <ShoppingCart size={18} />
            {lang === 'fr' ? 'Accéder au catalogue revendeur' : 'Go to reseller catalogue'}
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-950">
              {lang === 'fr' ? 'Commandes récentes' : 'Recent orders'}
            </h2>
            <button
              onClick={() => setPage('reseller-orders')}
              className="text-sm text-lodiss-red font-semibold hover:underline flex items-center gap-1"
            >
              {lang === 'fr' ? 'Voir tout' : 'View all'}
              <ChevronRight size={14} />
            </button>
          </div>

          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {lang === 'fr' ? 'Aucune commande' : 'No orders yet'}
            </p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(order => (
                <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-mono font-semibold text-gray-900">{order.id}</p>
                      <p className="text-sm text-gray-500">{formatDate(order.date)}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  <button
                    onClick={() => handleReorder(order)}
                    className="text-sm text-lodiss-red font-medium hover:underline"
                  >
                    {lang === 'fr' ? 'Commander à nouveau' : 'Reorder'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Account Manager Contact */}
        {stats.accountManager && (
          <div className="mt-6 bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-950 mb-4">
              {lang === 'fr' ? 'Votre contact LODISS' : 'Your LODISS contact'}
            </h2>
            <div className="flex items-center gap-6">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                <UserIcon size={24} className="text-gray-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{stats.accountManager.name}</p>
                <p className="text-sm text-gray-500">{stats.accountManager.email}</p>
                <p className="text-sm text-gray-500">{stats.accountManager.phone}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
